import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import {
  FaBroom,
  FaTruck,
  FaClipboardList,
  FaTrash,
  FaCheckCircle,
  FaArchive,
  FaSyncAlt,
  FaArrowUp,
  FaArrowDown,
  FaHome,
  FaExclamationTriangle,
} from "react-icons/fa";
import styles from "./RemoveStaledWbEntries.module.css";
import FloatingActions from "../../components/FloatingActions";

const getPNumber = (row) =>
  row?.P_NUMBER ?? row?.p_number ?? row?.P_Number ?? "";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#38bdf8" : "#d8e4f0",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(14,165,233,0.14)"
      : "none",
    fontSize: "0.96rem",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: "12px",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.92rem",
    fontWeight: state.isSelected ? 800 : 500,
  }),
};

const RemoveStaledWbEntries = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;
  const userCode = user?.emp_id || user?.id || user?.user_code || "SYSTEM";

  const navigate = useNavigate();
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const [openEntries, setOpenEntries] = useState([]);
  const [selectedPNumber, setSelectedPNumber] = useState("");
  const [reference, setReference] = useState(null);
  const [rows, setRows] = useState([]);

  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState("");

  const [toast, setToast] = useState("");
  const [popup, setPopup] = useState({ open: false, title: "", message: "" });

  const showPopup = (message, title = "Notice") =>
    setPopup({ open: true, title, message });

  const closePopup = () => setPopup({ open: false, title: "", message: "" });

  const selectedOption = useMemo(() => {
    const row = openEntries.find(
      (r) => String(getPNumber(r)) === String(selectedPNumber)
    );

    if (!row) return null;

    return {
      value: String(getPNumber(row)),
      label: `${row.P_DOCNUMBER || "-"} — ${row.P_TRUCK || "-"} — Status ${row.P_STATUS ?? "-"
        }`,
      row,
    };
  }, [openEntries, selectedPNumber]);

  const openEntryOptions = useMemo(() => {
    return openEntries
      .filter((r) => getPNumber(r))
      .map((r) => ({
        value: String(getPNumber(r)),
        label: `${r.P_DOCNUMBER || "-"} — ${r.P_TRUCK || "-"} — Status ${r.P_STATUS ?? "-"
          }`,
        row: r,
      }));
  }, [openEntries]);

  const fetchOpenEntries = async () => {
    try {
      setLoadingList(true);
      setOpenEntries([]);
      setSelectedPNumber("");
      setReference(null);
      setRows([]);

      const res = await axios.post("/api/wbauto/stale/openEntries", {
        emp_id,
        token,
      });

      const data = Array.isArray(res.data) ? res.data : [];
      setOpenEntries(data);

      if (data.length === 0) {
        setToast("No open WB entries found.");
      }
    } catch (err) {
      console.error("stale openEntries error:", err);
      showPopup(
        err?.response?.data?.message || "Failed to load open WB entries.",
        "Error"
      );
    } finally {
      setLoadingList(false);
    }
  };

  const fetchDetails = async (pNumber) => {
    if (!pNumber) {
      setReference(null);
      setRows([]);
      return;
    }

    try {
      setLoadingDetails(true);
      setReference(null);
      setRows([]);

      const res = await axios.post("/api/wbauto/stale/details", {
        emp_id,
        token,
        pNumber: Number(pNumber),
      });

      setReference(res.data?.reference || null);
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (err) {
      console.error("stale details error:", err);
      showPopup(
        err?.response?.data?.message || "Failed to load stale WB details.",
        "Error"
      );
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectReference = (opt) => {
    const pNumber = opt?.value || "";
    setSelectedPNumber(pNumber);

    if (pNumber) {
      fetchDetails(pNumber);
    } else {
      setReference(null);
      setRows([]);
    }
  };

  const runAction = async (row, actionType) => {
    const pNumber = getPNumber(row);

    if (!pNumber) {
      showPopup("P_NUMBER missing for selected record.", "Validation");
      return;
    }

    const labels = {
      DELETE: "delete this stale WB entry",
      COMPLETE_RECHECK: "complete recheck for this entry",
      MOVE_TO_DEL_PUR: "move this stale entry to DELE_PUR",
    };

    const ok = window.confirm(
      `Are you sure you want to ${labels[actionType]}?\n\nGate Entry: ${row.P_DOCNUMBER}\nVehicle: ${row.P_TRUCK}`
    );

    if (!ok) return;

    try {
      setActionLoading(`${actionType}_${pNumber}`);

      let url = "";
      const payload = {
        emp_id,
        token,
        pNumber: Number(pNumber),
        userCode,
      };

      if (actionType === "DELETE") {
        url = "/api/wbauto/stale/delete";
      } else if (actionType === "COMPLETE_RECHECK") {
        url = "/api/wbauto/stale/completeRecheck";
      } else if (actionType === "MOVE_TO_DEL_PUR") {
        url = "/api/wbauto/stale/moveToDelPur";
        payload.refPNumber = Number(selectedPNumber);
      } else {
        showPopup("Invalid action.", "Error");
        return;
      }

      const res = await axios.post(url, payload);

      if (res.data?.success) {
        setToast(`✅ ${res.data.message || "Action completed successfully."}`);
        if (actionType === "DELETE") {
          setReference(null);
          setRows([]);
        }
        else {
          await fetchDetails(selectedPNumber);
        }
      } else {
        showPopup(res.data?.message || "Action failed.", "Error");
      }
    } catch (err) {
      console.error("stale action error:", err);
      showPopup(
        err?.response?.data?.message || "Error while processing action.",
        "Error"
      );
    } finally {
      setActionLoading("");
    }
  };

  useEffect(() => {
    fetchOpenEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.loginBox}>
          <FaBroom className={styles.loginIcon} />
          <p>You must be logged in to remove stale WB entries.</p>
          <button className={styles.primaryBtn} onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div ref={topRef} />

      {toast && <div className={styles.toast}>{toast}</div>}

      {popup.open && (
        <div className={styles.modalBackdrop} onClick={closePopup}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h4>{popup.title}</h4>
            <p>{popup.message}</p>
            <button className={styles.primaryBtn} onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      <div className={styles.hero}>
        <div className={styles.heroIcon}>
          <FaBroom />
        </div>
        <div>
          <h2>Remove Staled WB Entries</h2>
          <p>Find open weighbridge records and safely clean stale transactions.</p>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.sectionTitle}>
          <FaClipboardList />
          <span>Select Correct Gate Entry</span>
        </div>

        <div className={styles.formItem}>
          <label>Correct Gate Entry / Vehicle</label>

          <Select
            styles={selectStyles}
            isClearable
            isSearchable
            isDisabled={loadingList}
            placeholder={
              loadingList
                ? "Loading last 1000 open entries..."
                : "Type Gate Entry No or Vehicle No..."
            }
            value={selectedOption}
            options={openEntryOptions}
            filterOption={(option, inputValue) => {
              const q = String(inputValue || "").toUpperCase();
              const r = option.data.row;

              return (
                String(r.P_DOCNUMBER || "").toUpperCase().includes(q) ||
                String(r.P_TRUCK || "").toUpperCase().includes(q)
              );
            }}
            onChange={handleSelectReference}
            noOptionsMessage={() => "No matching open WB entry found"}
          />
        </div>

        <button
          className={styles.refreshBtn}
          onClick={fetchOpenEntries}
          disabled={loadingList}
        >
          <FaSyncAlt />
          {loadingList ? "Refreshing..." : "Refresh Open Entries"}
        </button>

        <div className={styles.resultMeta}>
          {loadingList
            ? "Loading..."
            : `${openEntries.length} open entries loaded`}
        </div>
      </div>

      {loadingDetails && (
        <div className={styles.loadingBox}>Loading stale record details...</div>
      )}

      {reference && (
        <div className={styles.referenceCard}>
          <div className={styles.refIcon}>
            <FaTruck />
          </div>

          <div className={styles.refMain}>
            <h3>{reference.P_TRUCK || "-"}</h3>
            <p>{reference.P_DOCNUMBER || "-"}</p>
            <small>Reference P_NUMBER: {reference.P_NUMBER}</small>
          </div>

          <div className={styles.statusPill}>Status {reference.P_STATUS}</div>
        </div>
      )}

      {reference && (
        <div className={styles.panel}>
          <div className={styles.sectionTitle}>
            <FaExclamationTriangle />
            <span>Open Records for Vehicle</span>
          </div>

          <div className={styles.resultMeta}>
            Showing all open records where <strong>p_outdate is NULL</strong> for{" "}
            <strong>{reference.P_TRUCK}</strong>. Total: {rows.length}
          </div>

          {rows.length === 0 && (
            <div className={styles.emptyBox}>No related open records found.</div>
          )}

          {rows.map((row) => (
            <StaleCard
              key={getPNumber(row)}
              row={row}
              isReference={
                String(getPNumber(row)) === String(getPNumber(reference))
              }
              actionLoading={actionLoading}
              onAction={runAction}
            />
          ))}

          <div className={styles.exitPanel}>
            <button
              className={styles.exitBtn}
              type="button"
              onClick={() => navigate("/dashboard")}
            >
              <FaHome />
              Cancel / Exit to Menu
            </button>
          </div>
        </div>
      )}

      {!reference && !loadingDetails && (
        <div className={styles.emptyBox}>
          Select correct Gate Entry / Vehicle to show stale WB candidates.
        </div>
      )}

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

const StaleCard = ({ row, isReference, actionLoading, onAction }) => {
  const pNumber = getPNumber(row);
  const actionType = row.ACTION_TYPE || "NO_ACTION";
  const loading = actionLoading === `${actionType}_${pNumber}`;

  return (
    <div
      className={`${styles.staleCard} ${isReference ? styles.referenceHighlight : ""
        }`}
    >
      <div className={styles.cardTop}>
        <div>
          <h4>
            {row.P_DOCNUMBER || "-"}{" "}
            {isReference && <span className={styles.refTag}>REFERENCE</span>}
          </h4>
          <p>{row.P_TRUCK || "-"}</p>
        </div>

        <div className={`${styles.actionBadge} ${styles[actionType] || ""}`}>
          {getActionLabel(actionType)}
        </div>
      </div>

      <div className={styles.infoGrid}>
        <Info label="P Number" value={row.P_NUMBER} />
        <Info label="Sl No" value={row.P_SLNO} />
        <Info label="Status" value={row.P_STATUS} />
        <Info label="P Date" value={formatDate(row.P_DATE)} />

        <Info label="1st WB No" value={row.P_FIRSTWBNO} />
        <Info label="1st Weight" value={formatWeight(row.P_FIRSTWT)} />
        <Info label="1st Date" value={formatDate(row.P_FIRSTDATE)} />

        <Info label="2nd WB No" value={row.P_SECONDWBNO} />
        <Info label="2nd Weight" value={formatWeight(row.P_SECONDWT)} />
        <Info label="2nd Date" value={formatDate(row.P_SECONDDATE)} />

        <Info label="Net Weight" value={formatWeight(row.P_NETWT)} />
        <Info label="Recheck Req" value={row.P_RecheckReq} />
        <Info label="Recheck Status" value={row.P_RechekStatus} />
        <Info label="Recheck Date" value={formatDate(row.P_RechekDate)} />
        <Info label="Recheck Wt" value={formatWeight(row.P_RechekWt)} />
        <Info label="Item" value={row.P_Item_name} />
        <Info label="Party" value={row.P_Party_name} />
        <Info label="Transporter" value={row.p_Trans_name} />
      </div>

      <div className={styles.cardActions}>
        {actionType === "DELETE" && (
          <button
            className={styles.deleteBtn}
            disabled={loading}
            onClick={() => onAction(row, "DELETE")}
          >
            <FaTrash />
            {loading ? "Deleting..." : "Delete"}
          </button>
        )}

        {actionType === "COMPLETE_RECHECK" && (
          <button
            className={styles.recheckBtn}
            disabled={loading}
            onClick={() => onAction(row, "COMPLETE_RECHECK")}
          >
            <FaCheckCircle />
            {loading ? "Completing..." : "Complete Recheck"}
          </button>
        )}

        {actionType === "MOVE_TO_DEL_PUR" && (
          <button
            className={styles.moveBtn}
            disabled={loading}
            onClick={() => onAction(row, "MOVE_TO_DEL_PUR")}
          >
            <FaArchive />
            {loading ? "Moving..." : "Move to DELE_PUR"}
          </button>
        )}

        {actionType === "NO_ACTION" && (
          <div className={styles.noActionBox}>No action allowed for this record</div>
        )}
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className={styles.infoItem}>
    <span>{label}</span>
    <strong>{value || "-"}</strong>
  </div>
);

function getActionLabel(actionType) {
  if (actionType === "DELETE") return "Delete";
  if (actionType === "COMPLETE_RECHECK") return "Recheck";
  if (actionType === "MOVE_TO_DEL_PUR") return "Move";
  return "No Action";
}

function formatDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d)) return "-";

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatWeight(v) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (isNaN(n)) return v;
  return `${n.toFixed(3)} MT`;
}

export default RemoveStaledWbEntries;